import { OrderProps } from "store/modules/Orders/types.store";
import { CoverageAreaOrderDetailDto } from "types/coverageAreaOrderDetail";
import { ECoverageAreaType } from "enums/options/coverageArea";

const FINALIZED_TAB = "3";
const DEALER_STORE_ORIGIN = "DealerStore";
const DRIVER_STORE_ORIGIN = "DriverStore";

export type CoverageAreaMapType = "dealerStore" | "driverStore" | "delivery";

export interface OrderDriverPermissions {
  canSelectDriver: boolean;
  canChangeDriver: boolean;
}

export interface ResolvedOrderDriverDisplay {
  name?: string;
  surname?: string;
  hasVisibleDriver: boolean;
}

function trimPart(value?: string | null): string {
  return (value ?? "").trim();
}

function hasVisibleDriverName(
  driver?: { name?: string | null; surname?: string | null } | null,
): boolean {
  if (!driver) return false;
  return Boolean(trimPart(driver.name) || trimPart(driver.surname));
}

function isDriverStoreCoverageArea(
  placedOrigin?: string,
  coverageArea?: OrderProps["coverageArea"],
): boolean {
  if (placedOrigin === DRIVER_STORE_ORIGIN) return true;

  const coverageType = coverageArea?.type ?? coverageArea?.areaType;
  return (
    coverageType === ECoverageAreaType.DriverStore ||
    coverageType === "DriverStore" ||
    coverageType === "driverStore"
  );
}

export function orderHasAssignedDriver(driver?: OrderProps["driver"]): boolean {
  if (!trimPart(driver?.id)) return false;
  return hasVisibleDriverName(driver);
}

export function resolveCoverageAreaMapType({
  placedOrigin,
}: {
  placedOrigin?: string;
}): CoverageAreaMapType {
  if (placedOrigin === DEALER_STORE_ORIGIN) {
    return "dealerStore";
  }

  if (placedOrigin === DRIVER_STORE_ORIGIN) {
    return "driverStore";
  }

  return "delivery";
}

export function resolveDriverStoreCoverageDriver({
  placedOrigin,
  coverageArea,
}: {
  placedOrigin?: string;
  coverageArea?: OrderProps["coverageArea"];
}): { name?: string; surname?: string } | null {
  if (!isDriverStoreCoverageArea(placedOrigin, coverageArea)) {
    return null;
  }

  const name = trimPart(coverageArea?.name);
  if (!name) return null;

  return { name };
}

export function getCoverageAreaPreAssignedDriver(
  coverageAreaDetail: CoverageAreaOrderDetailDto | null,
): { driverId?: string } | null {
  if (!coverageAreaDetail) return null;

  const sortedDrivers = (coverageAreaDetail.drivers ?? [])
    .slice()
    .sort(
      (left, right) =>
        (left.priority ?? Number.MAX_SAFE_INTEGER) -
        (right.priority ?? Number.MAX_SAFE_INTEGER),
    );

  const primaryDriverId = sortedDrivers[0]?.driverId;
  if (!primaryDriverId) return null;

  return { driverId: primaryDriverId };
}

export function resolveOrderDriverDisplay({
  orderDriver,
  placedOrigin,
  coverageArea,
}: {
  orderDriver?: OrderProps["driver"];
  placedOrigin?: string;
  coverageArea?: OrderProps["coverageArea"];
}): ResolvedOrderDriverDisplay {
  if (orderHasAssignedDriver(orderDriver)) {
    return {
      name: orderDriver?.name,
      surname: orderDriver?.surname,
      hasVisibleDriver: true,
    };
  }

  const preAssignedDriver = resolveDriverStoreCoverageDriver({
    placedOrigin,
    coverageArea,
  });
  if (preAssignedDriver) {
    return {
      name: preAssignedDriver.name,
      surname: preAssignedDriver.surname,
      hasVisibleDriver: true,
    };
  }

  return { hasVisibleDriver: false };
}

export function formatOrderDriverLabel({
  orderDriver,
  placedOrigin,
  coverageArea,
}: {
  orderDriver?: OrderProps["driver"];
  placedOrigin?: string;
  coverageArea?: OrderProps["coverageArea"];
}): string | null {
  const display = resolveOrderDriverDisplay({
    orderDriver,
    placedOrigin,
    coverageArea,
  });

  if (!display.hasVisibleDriver) return null;

  return [display.name, display.surname].filter(Boolean).join(" ") || null;
}

export function hasOrderPreAssignedDriver({
  placedOrigin,
  coverageArea,
}: {
  placedOrigin?: string;
  coverageArea?: OrderProps["coverageArea"];
}): boolean {
  return (
    resolveDriverStoreCoverageDriver({ placedOrigin, coverageArea }) !== null
  );
}

export function hasCoveragePreAssignedDriver(
  coverageAreaDetail: CoverageAreaOrderDetailDto | null,
): boolean {
  if (!coverageAreaDetail) return false;
  if (coverageAreaDetail.hasDriver) return true;
  return (coverageAreaDetail.drivers ?? []).some((row) =>
    Boolean(row.driverId),
  );
}

export function getOrderDriverPermissions({
  mapType,
  currentTab,
  hasOrderDriver,
  hasPreAssignedDriver = false,
}: {
  mapType: CoverageAreaMapType;
  currentTab?: string;
  hasOrderDriver: boolean;
  hasPreAssignedDriver?: boolean;
}): OrderDriverPermissions {
  if (currentTab === FINALIZED_TAB) {
    return { canSelectDriver: false, canChangeDriver: false };
  }

  if (mapType === "dealerStore") {
    return { canSelectDriver: false, canChangeDriver: false };
  }

  if (mapType === "driverStore") {
    return {
      canSelectDriver: !hasOrderDriver && !hasPreAssignedDriver,
      canChangeDriver: true,
    };
  }

  return { canSelectDriver: true, canChangeDriver: true };
}

export function canShowDriverAction(
  permissions: OrderDriverPermissions,
  hasOrderDriver: boolean,
  hasPreAssignedDriver = false,
): boolean {
  const hasVisibleDriver = hasOrderDriver || hasPreAssignedDriver;

  return (
    (!hasVisibleDriver && permissions.canSelectDriver) ||
    (hasVisibleDriver && permissions.canChangeDriver)
  );
}

export function isStoreOrderMapType(mapType: CoverageAreaMapType): boolean {
  return mapType === "dealerStore" || mapType === "driverStore";
}
